/**
 * Cloud Connection Management Service
 * 
 * This service handles connection verification, clearing, and management
 * for cloud storage operations.
 */

import cloudConnectionService from './cloudConnectionService'
import cloudTokenService from './cloudTokenService'
import cloudReconnectionService from './cloudReconnectionService'

class CloudConnectionManagementService {
  /**
   * Verify cloud connection status with backend
   */
  async verifyCloudConnectionWithBackend(authToken, setStateFunctions) {
    const {
      setIsConnected,
      setConnectedProvider,
      setAccessToken,
      setRefreshToken,
      setUserInfo,
      setLoading
    } = setStateFunctions

    console.log('🚀 CloudConnectionManagement: verifyCloudConnectionWithBackend called with authToken:', !!authToken)
    
    try {
      setLoading(true)
      
      const data = await cloudConnectionService.verifyCloudConnectionWithBackend(authToken)
      
      if (data) {
        console.log('✅ CloudConnectionManagement: Backend verification response:', data)
        
        if (data.connected) {
          // Backend confirms connection
          setConnectedProvider('google_drive')
          setIsConnected(true)
          
          // Store tokens and user info
          if (data.userInfo) {
            setUserInfo(data.userInfo)
          }
          
          if (data.accessToken) {
            setAccessToken(data.accessToken)
            cloudTokenService.updateGoogleDriveAPI(data.accessToken)
            cloudTokenService.dispatchTokenReadyEvent(data.accessToken, data.userInfo)
          }
          
          if (data.refreshToken) {
            setRefreshToken(data.refreshToken)
          }
          
          cloudTokenService.storeTokens(data.accessToken, data.refreshToken, data.userInfo)
          console.log('✅ CloudConnectionManagement: Google Drive connection verified and restored with token')
        } else {
          // Backend says not connected, but may still provide valid tokens
          console.log('❌ CloudConnectionManagement: Backend reports no connection, clearing access data but preserving refresh token')
          
          if (data.userInfo && data.accessToken) {
            console.log('🔑 CloudConnectionManagement: Backend provided tokens despite not connected - using them')
            setConnectedProvider('google_drive')
            setUserInfo(data.userInfo)
            setAccessToken(data.accessToken)
            cloudTokenService.storeTokens(data.accessToken, data.refreshToken, data.userInfo)
            cloudTokenService.dispatchTokenReadyEvent(data.accessToken, data.userInfo)
          } else {
            // Try to refresh tokens using existing refresh token if available
            const storedTokens = cloudTokenService.getStoredTokens()
            if (storedTokens.refreshToken) {
              console.log('🔄 CloudConnectionManagement: Backend says not connected, but we have a refresh token - attempting token refresh...')
              try {
                const newAccessToken = await cloudConnectionService.attemptTokenRefresh()
                if (newAccessToken) {
                  console.log('✅ CloudConnectionManagement: Successfully refreshed tokens using existing refresh token')
                  setConnectedProvider('google_drive')
                  setAccessToken(newAccessToken)
                  cloudTokenService.storeTokens(newAccessToken, storedTokens.refreshToken, data.userInfo)
                  cloudTokenService.dispatchTokenReadyEvent(newAccessToken, data.userInfo)
                  return
                }
              } catch (refreshError) {
                console.warn('⚠️ CloudConnectionManagement: Failed to refresh tokens using existing refresh token:', refreshError)
              }
            }
            
            this.clearConnectionData(setStateFunctions, true) // Preserve refresh token during temporary issues
          }
          
          // Schedule reconnection if not in onboarding
          if (!cloudConnectionService.isInOnboarding()) {
            cloudReconnectionService.scheduleReconnectAttempt()
          }
        }
      } else {
        console.log('❌ CloudConnectionManagement: Backend verification failed')
        setIsConnected(false)
        
        if (!cloudConnectionService.isInOnboarding()) {
          cloudReconnectionService.scheduleReconnectAttempt()
        }
      }
    } catch (error) {
      console.error('❌ CloudConnectionManagement: Error verifying backend connection:', error)
      setIsConnected(false)
      
      if (!cloudConnectionService.isInOnboarding()) {
        cloudReconnectionService.scheduleReconnectAttempt()
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Clear connection data and state
   */
  clearConnectionData(setStateFunctions, preserveRefreshToken = false) {
    const {
      setIsConnected,
      setConnectedProvider,
      setAccessToken,
      setRefreshToken,
      setUserInfo
    } = setStateFunctions

    // CRITICAL: Check if user is authenticated before clearing connection data
    if (!cloudConnectionService.isUserAuthenticated()) {
      console.log('🔒 CloudConnectionManagement: User not authenticated, blocking connection data clearing')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    if (cloudConnectionService.isInOnboarding()) {
      console.log('🔒 CloudConnectionManagement: Onboarding detected, skipping connection data clearing')
      throw new Error('Connection data clearing not available during onboarding')
    }
    
    setIsConnected(false)
    setConnectedProvider(preserveRefreshToken ? 'google_drive' : null) // Preserve provider for token refresh
    setAccessToken(null)
    setRefreshToken(preserveRefreshToken ? null : null) // This will be set by the caller
    setUserInfo(null)
    
    if (preserveRefreshToken) {
      cloudTokenService.clearAccessToken()
      console.log('🔐 CloudConnectionManagement: Preserving refresh token during temporary connection issue')
    } else {
      cloudTokenService.clearAllTokens()
      console.log('🗑️ CloudConnectionManagement: Cleared all tokens (permanent cleanup)')
    }
  }

  /**
   * Clear all cloud tokens and reset state
   */
  clearCloudTokens(setStateFunctions) {
    const {
      setIsConnected,
      setConnectedProvider,
      setAccessToken,
      setRefreshToken,
      setUserInfo
    } = setStateFunctions

    // CRITICAL: Check if user is authenticated before clearing tokens
    if (!cloudConnectionService.isUserAuthenticated()) {
      console.log('🔒 CloudConnectionManagement: User not authenticated, blocking token clearing')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    if (cloudConnectionService.isInOnboarding()) {
      console.log('🔒 CloudConnectionManagement: Onboarding detected, skipping token clearing')
      throw new Error('Token clearing not available during onboarding')
    }
    
    console.log('🧹 CloudConnectionManagement: Clearing all cloud tokens')
    
    // Clear tokens and reset state
    cloudTokenService.clearAllTokens()
    setIsConnected(false)
    setConnectedProvider(null)
    setAccessToken(null)
    setRefreshToken(null)
    setUserInfo(null)
    
    // Update GoogleDriveAPI if it exists
    cloudTokenService.updateGoogleDriveAPI(null)
    
    console.log('✅ CloudConnectionManagement: All cloud tokens cleared')
  }

  /**
   * Set up automatic reconnection timer
   */
  setupReconnectionTimer(accessToken, refreshToken) {
    // Only set up reconnection if we have tokens
    if (accessToken && refreshToken) {
      console.log('⏰ CloudConnectionManagement: Setting up automatic reconnection timer')
      cloudReconnectionService.setupReconnectionTimer(
        accessToken, 
        refreshToken, 
        cloudConnectionService.isInOnboarding()
      )
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  scheduleReconnectAttempt() {
    cloudReconnectionService.scheduleReconnectAttempt(cloudConnectionService.isInOnboarding())
  }

  /**
   * Reset reconnection state (useful for manual recovery)
   */
  resetReconnectionState() {
    cloudReconnectionService.resetReconnectionState(cloudConnectionService.isInOnboarding())
  }

  /**
   * Force stop all reconnection attempts (emergency stop)
   */
  stopReconnectionAttempts() {
    cloudReconnectionService.stopReconnectionAttempts(cloudConnectionService.isInOnboarding())
  }
}

export default new CloudConnectionManagementService()
